import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useCallback, useRef, useState } from 'react';

type TurnstileAppearance = 'always' | 'execute' | 'interaction-only';

/**
 * Renders a Cloudflare Turnstile challenge and keeps a fresh token ready.
 *
 * `appearance: 'interaction-only'` keeps the widget's box collapsed to nothing whenever Cloudflare
 * can verify the visitor non-interactively, and only expands it when a real challenge is needed -
 * this is the closest thing to a fully invisible widget the client can ask for. Callers with more
 * room to spare can pass `'always'` instead to keep the widget visible. The theme is read off the
 * nearest ancestor's `data-color-scheme` so it matches whatever surface it's rendered on.
 *
 * `containerRef` must be attached to the element the widget should render into, and `widget` must
 * be rendered as a child of that element.
 *
 * Tokens are single-use: callers must call `consumeToken()` right before posting, which resets
 * the widget so it fetches the next one.
 */
export const useTurnstile = (siteKey: string | undefined, appearance: TurnstileAppearance = 'interaction-only') => {
    // A callback ref (not useRef) so we re-render once the container mounts and its
    // `data-color-scheme` ancestor becomes readable.
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const containerRef = useCallback((node: HTMLDivElement | null) => setContainer(node), []);

    const widgetRef = useRef<TurnstileInstance | null>(null);
    const [token, setToken] = useState<string | null>(null);
    // Only tracks the *initial* verification, not the silent re-verify after consumeToken() or an
    // expired token - those happen while the form is hidden or already interactive, so surfacing a
    // "loading" state for them would just flicker.
    const [isLoading, setIsLoading] = useState(() => !!siteKey);
    // True while Cloudflare is showing an interactive challenge (e.g. a checkbox) the visitor must
    // act on - only happens for a minority of visitors, but when it does the widget expands out of
    // its normally-invisible state, so callers can point the visitor at it.
    const [needsInteraction, setNeedsInteraction] = useState(false);

    const consumeToken = useCallback(() => {
        const current = token;
        setToken(null);
        widgetRef.current?.reset();
        return current;
    }, [token]);

    const theme =
        container?.closest('[data-color-scheme]')?.getAttribute('data-color-scheme') === 'dark' ? 'dark' : 'light';

    const widget =
        siteKey && container ? (
            <Turnstile
                ref={widgetRef}
                siteKey={siteKey}
                options={{ size: 'flexible', appearance, theme }}
                onSuccess={(t) => {
                    setToken(t);
                    setIsLoading(false);
                }}
                onExpire={() => setToken(null)}
                onError={() => {
                    setToken(null);
                    setIsLoading(false);
                }}
                onBeforeInteractive={() => setNeedsInteraction(true)}
                onAfterInteractive={() => setNeedsInteraction(false)}
            />
        ) : null;

    return { containerRef, token, isLoading, needsInteraction, consumeToken, widget };
};
